import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

/**
 * VideoModal — auto-plays a video with sound in a centered modal on page load.
 * Auto-detects YouTube / Vimeo / direct file URLs. Plays once (no loop).
 * Shows only once per session per `storageKey` so repeat visitors aren't nagged.
 */

const YT_RX = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/i;
// Match plain vimeo (vimeo.com/123456789), unlisted (vimeo.com/123/HASH),
// player links (player.vimeo.com/video/123?h=HASH), and channel/showcase paths.
const VIMEO_RX = /(?:player\.)?vimeo\.com\/(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/)?(\d+)(?:\/([a-zA-Z0-9]+))?/i;
const FILE_RX = /\.(mp4|webm|mov|ogg|m4v)(\?|#|$)/i;

const getVimeoHash = (url) => {
  // Explicit ?h= parameter takes precedence over path-suffix hash
  try {
    const u = new URL(url);
    const h = u.searchParams.get("h");
    if (h) return h;
  } catch { /* not a URL, fall through */ }
  return null;
};

const getEmbed = (url) => {
  if (!url) return null;
  const yt = url.match(YT_RX);
  if (yt) {
    return { kind: "iframe", src: `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0&modestbranding=1` };
  }
  const vm = url.match(VIMEO_RX);
  if (vm) {
    const id = vm[1];
    const hash = getVimeoHash(url) || vm[2] || "";
    const params = new URLSearchParams({ autoplay: "1", dnt: "1" });
    if (hash) params.set("h", hash);
    return { kind: "iframe", src: `https://player.vimeo.com/video/${id}?${params.toString()}` };
  }
  if (FILE_RX.test(url) || url.startsWith("/api/files/") || url.includes("/api/files/")) {
    return { kind: "file", src: url };
  }
  // Fallback: assume it's an iframe-embeddable URL
  return { kind: "iframe", src: url };
};

const VideoModal = ({ url, title = "Watch the video" }) => {
  const [open, setOpen] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!url) return;
    // Play on every page load — small delay so the hero settles first.
    const id = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(id);
  }, [url]);

  const close = () => {
    setOpen(false);
    if (videoRef.current) {
      try { videoRef.current.pause(); } catch { /* ignore */ }
    }
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    // Lock scroll while modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || !url) return null;
  const embed = getEmbed(url);
  if (!embed) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-8 bg-slate-950/85 backdrop-blur-md animate-fade-up"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="video-modal"
    >
      <div
        className="relative w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4 z-10 h-10 w-10 sm:h-11 sm:w-11 rounded-full bg-white text-slate-950 shadow-2xl flex items-center justify-center hover:scale-105 transition"
          aria-label="Close video"
          data-testid="video-modal-close"
        >
          <X className="h-5 w-5" strokeWidth={2.5} />
        </button>
        <div className="relative aspect-video w-full rounded-2xl overflow-hidden border border-white/10 bg-black shadow-[0_20px_80px_-20px_rgba(30,144,255,0.6)]">
          {embed.kind === "iframe" ? (
            <iframe
              src={embed.src}
              title={title}
              className="absolute inset-0 h-full w-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
          ) : (
            <video
              ref={videoRef}
              src={embed.src}
              className="absolute inset-0 h-full w-full object-contain bg-black"
              autoPlay
              controls
              playsInline
              onEnded={close}
              data-testid="video-modal-player"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoModal;
